import { useEffect, useState } from 'react';

import { WinnersFeed } from '@fanduel/casino-swj-consumer';
import type { WinnersFeedResponse } from '@fanduel/casino-swj-consumer';
import type { TierWinner } from '@/types/zones/slices/site-wide-jackpot-slice';

import { DD_USE_CASES, type IddUseCases, type TRumError } from '@/utils/datadog/types';
import { ddRumAction } from '@/utils/datadog';

import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies';
import { getConfig } from '@/utils/config/config';
import { maskString } from '@/utils/text/mask-string';
import { timeAgo } from '@/utils/date/get-hour-from-date';
import { buildApiUrl } from '@/utils/url/build-url';

const ddErrorSwjWinnersFeed = (context: { jackpotId: string }, use_case: IddUseCases): TRumError => ({
    name: 'SWJ_GET_WINNERS_FEED_FAILED',
    description: 'Site wide jackpot get winners feed failed',
    context,
    use_case
});

export const useWinnersFeed = (jackpotId: string) => {
    const [tierWinners, setTierWinners] = useState<TierWinner[]>([]);

    const config = getConfig();
    const region = getCookieValue(COOKIES.REGION) as string;
    const apiUrl = buildApiUrl(config?.apis?.SiteWideJackpotClient).replace('statePlaceholder', region);

    const mapData = (data?: WinnersFeedResponse) => {
        if (!data) {
            return;
        }

        const matchingJackpot = data.find((d) => d?.jackpotRef === jackpotId);

        if (!matchingJackpot) {
            setTierWinners([]);
            return;
        }

        const winners: TierWinner[] = matchingJackpot.latestWins.map((winner) => ({
            ...winner,
            username: maskString(winner.username),
            amount: winner.amount,
            tierName: winner.tierName,
            timeAgo: timeAgo(winner.timestamp)
        }));

        setTierWinners(winners);
    };

    const winnersFeed = new WinnersFeed({
        host: apiUrl,
        region,
        jackpotIds: [jackpotId],
        interval: 0,
        numberOfUsers: 10,
        callback: mapData,
        error: () => ddRumAction(ddErrorSwjWinnersFeed({ jackpotId: jackpotId }, DD_USE_CASES.SITE_WIDE_JACKPOT))
    });

    useEffect(() => {
        winnersFeed.connect();

        return () => winnersFeed.disconnect();
    }, []);

    return { tierWinners, mapData };
};
